<script lang="ts">
import { useTailwind } from '@/plugins/useTailwind'
import type { SwitchOptions } from '@/types'
import { type TThemeValue, Themes } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'
import { useRouter } from 'vue-router'

/**
 * A switch element which toggles between different options.
 */
export default defineComponent({
  props: {
    /**
     * Name of the switch element.
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Model prop to v-bind to.
     */
    modelValue: {
      type: String,
      required: true,
    },
    /**
     * Possible options.
     */
    options: {
      type: Array<SwitchOptions>,
      required: true,
    },
    /**
     * The theme to colorise the component.
     */
    theme: {
      type: String as PropType<TThemeValue>,
      default: Themes.Light,
    },
  },
  emits: ['update:modelValue', 'change'],
  setup() {
    return {
      router: useRouter(),
      tailwind: useTailwind(),
    }
  },
  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value: string) {
        this.$emit('update:modelValue', value)
        this.$emit('change', value)
      },
    },
    pillWidth(): string {
      return `calc(100% / ${this.options.length})`
    },
    selectedIndex(): number {
      return this.options.findIndex(
        (option) => option.value === this.modelValue
      )
    },
    pillLeft(): string {
      let padding = '0px'

      if (this.selectedIndex === 0) {
        padding = this.tailwind.padding[1]
      } else if (this.selectedIndex === this.options.length - 1) {
        padding = `-${this.tailwind.padding[1]}`
      }

      return `calc(${this.selectedIndex} * ${this.pillWidth} + ${padding})`
    },
  },
})
</script>

<template>
  <div class="switch-options rounded-100 bg-primary-lighter p-1" :class="theme">
    <label
      v-for="(option, key) in options"
      :key
      v-tooltip="{
        content: option.tooltip,
        shown: true,
        flip: false,
        container: '.switch-options',
      }"
      :class="{
        selected: option.value === value,
      }"
      :for="`${name}-${key}`"
      class="relative inline-flex flex-1 items-center justify-center p-2 hover:cursor-pointer font-bold"
    >
      <input
        :id="`${name}-${key}`"
        v-model="value"
        :name="`${name}-${key}`"
        :value="option.value"
        type="radio"
      />

      {{ option.label }}
    </label>
  </div>
</template>

<style lang="scss" scoped>
input[type='radio'] {
  appearance: none;
  display: none;
}

.dark-section .switch-options,
.dark.switch-options {
  @apply relative flex bg-slate-600 text-center text-pure-white;

  &::before {
    @apply bg-pure-white;
  }

  .selected {
    @apply text-black;
  }
}

.switch-options {
  @apply relative flex text-center;

  &::before {
    content: '';
    width: v-bind(pillWidth);
    height: calc(100% - theme('padding.2'));
    left: v-bind(pillLeft);
    transition: 0.2s linear all;

    @apply absolute top-1 block rounded-100 bg-pure-white;
  }
}
</style>
